import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import Slide from "@mui/material/Slide";
import { TransitionProps } from "@mui/material/transitions";
import { Box, Grid, Typography } from "@mui/material";
import { Cancel, VpnKey } from "@mui/icons-material";
import { useForm } from "react-hook-form";

import Button from "./Button";
import CustomTextField from "./CustomTextField";
import { useModalStyles } from "./useModalStyles";
import { useAppDispatch } from "../../redux/slice/redux-hooks";
import { inactivateAgencyById } from "../../redux/slice/agency/AgencySlice";

interface DeleteAgencyPopupProps {
  open: boolean;
  onClose: () => void;
  agencyData: any;
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const DeleteAgencyPopup: React.FC<DeleteAgencyPopupProps> = ({
  open,
  onClose,
  agencyData,
}) => {
  const { control, handleSubmit } = useForm({});
  const dispatch = useAppDispatch();
  const [enableButton, setEnableButton] = useState(true);
  const [inputData, setInputData] = useState("");

  const onSubmit = async () => {
    await dispatch(inactivateAgencyById(agencyData?.agencyId));
    onClose();
  };

  const handleClose = () => {
    onClose();
  };

  const {
    paperSx,
    headerSx,
    bodySx,
    footerSx,
    tokens,
    isDark,
    inputFieldSx,
    primaryBtnSx,
    cancelBtnSx,
  } = useModalStyles();

    const FieldLabel = ({ label, required = false }: { label: string; required?: boolean }) => (
      <Typography
        sx={{
          fontSize: "0.8rem",
          fontWeight: 600,
          color: tokens.textSecondary,
          mb: 0.7,
          display: "flex",
          alignItems: "center",
          gap: 0.3,
        }}
      >
        {label}
        {required && (
          <Box component="span" sx={{ color: tokens.accent }}>*</Box>
        )}
      </Typography>
    );

  useEffect(() => {
    if (inputData === agencyData?.agencyName) {
      setEnableButton(false);
    } else {
      setEnableButton(true);
    }
  }, [agencyData?.agencyName, inputData]);

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      onClose={onClose}
      aria-describedby="alert-dialog-slide-description"
      onClick={(event) => {
        event.stopPropagation();
      }}
      PaperProps={{
        sx: {
          ...paperSx(false),
          width: 640,
          maxWidth: "90vw",
          color: tokens.textPrimary,
        },
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={headerSx}>
          <Typography
            fontWeight={700}
            sx={{
              fontSize: "1.1rem",
              color: tokens.textPrimary,
              lineHeight: 1.3,
            }}
          >
            Inactivate Agency
          </Typography>
        </Box>

        <Box sx={{ ...bodySx, color: tokens.textSecondary, px: 3.5, py: 3.5 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography>
                {`Are you sure you want to inactivate agency "${agencyData?.agencyName}" ?`}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Box
                sx={{
                  background: isDark ? "rgba(255,176,32,0.08)" : "rgba(255,176,32,0.06)",
                  border: `1px solid ${isDark ? "rgba(255,176,32,0.22)" : "rgba(255,176,32,0.35)"}`,
                  borderRadius: "10px",
                  px: 2,
                  py: 1.5,
                  color: isDark ? "#ffc857" : "#b45309",
                  fontSize: "14px",
                  lineHeight: 1.6,
                }}
              >
                The data provided below will also get affected.
              </Box>
            </Grid>

            {agencyData?.practices?.length > 0 && (
              <Grid item xs={12}>
                <Typography
                  sx={{ color: tokens.textSecondary }}
                  component="span"
                >
                  Practice:
                </Typography>
                <Typography sx={{ color: tokens.textPrimary, ml: 0.5 }} component="span">
                  [{agencyData?.practices?.map((data: any) => data).join(", ")}]
                </Typography>
              </Grid>
            )}

            {agencyData?.agencyUsers?.length > 0 && (
              <Grid item xs={12}>
                <Typography
                  sx={{ color: tokens.textSecondary }}
                  component="span"
                >
                  Users:
                </Typography>
                <Typography sx={{ color: tokens.textPrimary, ml: 0.5 }} component="span">
                  [
                  {agencyData?.agencyUsers?.map((data: any) => data).join(", ")}
                  ]
                </Typography>
              </Grid>
            )}

            {agencyData?.productCommissions?.length > 0 && (
              <Grid item xs={12}>
                <Typography
                  sx={{ color: tokens.textSecondary }}
                  component="span"
                >
                  Products:
                </Typography>
                <Typography sx={{ color: tokens.textPrimary, ml: 0.5 }} component="span">
                  [
                  {agencyData?.productCommissions
                    ?.map((data: any) => data)
                    .join(", ")}
                  ]
                </Typography>
              </Grid>
            )}

            <Grid item xs={12}>
              <Typography sx={{ color: tokens.textPrimary }}>
                Type{" "}
                <Typography
                  component="span"
                  sx={{ color: tokens.accent, fontSize: "20px", fontWeight: 700 }}
                >
                  {agencyData?.agencyName}
                </Typography>{" "}
                to confirm your action.
              </Typography>
            </Grid>

            <Grid item xs={12}>
               <FieldLabel label="Agency Name" />
              <CustomTextField
                control={control}
                controllerName="agencyName"
                label="Agency Name"
                sx={inputFieldSx(false)}
                onChange={(e) => {
                  const inputAgencyName = e.target.value;
                  setInputData(inputAgencyName);
                }}
              />
            </Grid>
          </Grid>
        </Box>

        <Box sx={footerSx}>
          <Grid container justifyContent="flex-end" spacing={2}>
            <Grid item xs={12} sm="auto">
              <Button
                type="submit"
                disabled={enableButton}
                startIcon={<VpnKey />}
                fullWidth
                sx={{ ...primaryBtnSx }}
              >
                Inactivate
              </Button>
            </Grid>
            <Grid item xs={12} sm="auto">
                            <Button
                type="button"
                variant="outlined"
                onClick={onClose}
                startIcon={<Cancel sx={{ fontSize: "1.1rem" }} />}
                sx={{ ...cancelBtnSx, minWidth: { xs: "100%", sm: 120 } }}
              >
                Cancel
              </Button>
            </Grid>
          </Grid>
        </Box>
      </form>
    </Dialog>
  );
};

export default DeleteAgencyPopup;